import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const BG_IMG =
    "https://static.prod-images.emergentagent.com/jobs/a18dbc73-dfe3-40c8-91fa-792e4d8d7256/images/8bf4210ea4811ddd813cae1732e4d294a54283014781b07a625d75e4c7bb2e51.png";

export default function Login() {
    const { user, login, loading } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [setupRequired, setSetupRequired] = useState(false);
    const [checking, setChecking] = useState(true);

    useEffect(() => {
        api.get("/auth/setup-status")
            .then((r) => setSetupRequired(r.data.setup_required))
            .finally(() => setChecking(false));
    }, []);

    if (loading || checking) {
        return (
            <div className="flex h-screen items-center justify-center bg-background">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
        );
    }

    if (user) return <Navigate to="/" replace />;
    if (setupRequired) return <Navigate to="/setup" replace />;

    const onSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await login(email, password);
            toast.success("Welcome back");
            navigate("/");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="grid min-h-screen grid-cols-1 lg:grid-cols-2 bg-background">
            {/* Left – Form */}
            <div className="flex items-center justify-center px-6 py-12">
                <div className="w-full max-w-md">
                    <div className="mb-10 flex items-center gap-3">
                        <div className="grid h-11 w-11 place-items-center rounded-xl bg-primary text-primary-foreground shadow-sm">
                            <span className="font-display text-lg font-extrabold">C</span>
                        </div>
                        <div>
                            <div className="font-display text-2xl font-extrabold tracking-tight">
                                Credo SDC
                            </div>
                        </div>
                    </div>

                    <h1 className="font-display text-4xl font-extrabold tracking-tight text-foreground sm:text-5xl">
                        Welcome back.
                    </h1>
                    <p className="mt-3 text-sm text-muted-foreground">
                        Sign in to manage your engagements, bills, and reimbursables.
                    </p>

                    <form onSubmit={onSubmit} className="mt-10 space-y-5" data-testid="login-form">
                        <div className="space-y-2">
                            <Label htmlFor="email">Email</Label>
                            <Input
                                id="email"
                                type="email"
                                placeholder="you@firm.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                autoComplete="email"
                                data-testid="login-email-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="password">Password</Label>
                            <div className="relative">
                                <Input
                                    id="password"
                                    type={showPw ? "text" : "password"}
                                    placeholder="••••••••"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    autoComplete="current-password"
                                    data-testid="login-password-input"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPw((s) => !s)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                    aria-label="Toggle password visibility"
                                >
                                    {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            </div>
                        </div>

                        <Button
                            type="submit"
                            className="w-full h-11"
                            disabled={submitting}
                            data-testid="login-submit-button"
                        >
                            {submitting ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                "Sign in"
                            )}
                        </Button>
                    </form>

                    <p className="mt-10 text-xs text-muted-foreground">
                        Don't have an account?{" "}
                        <span className="font-medium text-foreground">
                            Ask your admin to invite you.
                        </span>
                    </p>
                </div>
            </div>

            {/* Right – Visual */}
            <div className="relative hidden lg:block overflow-hidden">
                <img
                    src={BG_IMG}
                    alt=""
                    className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-[#4A1D7A]/85 via-[#5B2DA0]/55 to-transparent" />
                <div className="relative z-10 flex h-full flex-col justify-end p-12 text-white">
                    <div className="max-w-md">
                        <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/10 px-3 py-1 text-[11px] uppercase tracking-[0.18em]">
                            For Chartered Accountants
                        </div>
                        <h2 className="font-display text-4xl font-extrabold leading-tight tracking-tight">
                            Never forget to bill or claim a reimbursable again.
                        </h2>
                        <p className="mt-4 text-sm text-white/80">
                            SDC keeps every engagement, deadline, and billing checkpoint visible —
                            so your practice runs on rails.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
